import { BsChevronRight } from 'react-icons/bs';
import { useTranslation } from 'next-i18next';
import ImageWithFallback from '../../common/ImageWithFallback';
import { REGIONS, RegionEnum } from '../../../core/japaneseRegions';
import testIds from './testIds';
import Link from '../../common/Link/Link';

type MapCardHeaderProps = {
  selectedArea: RegionEnum;
  onSearchClick: (area: string) => void;
  searchQuery: string;
};

const MapCardHeader = ({ selectedArea, onSearchClick, searchQuery }: MapCardHeaderProps) => {
  const { t } = useTranslation();

  const regionText = (
    <button
      type="button"
      onClick={() => onSearchClick(REGIONS[selectedArea]?.path as string)}
      className="flex cursor-pointer items-center justify-between"
    >
      <div className="flex">
        <p className="text-small font-bold hover:underline">
          {t('component:map_card.search_in', { area: t(REGIONS[selectedArea]?.name as string) })}
        </p>
      </div>
      <BsChevronRight className="mr-7 hidden size-3 stroke-2 text-orange-700 lg:block" />
    </button>
  );

  return (
    <>
      <div className="size-20" style={{ filter: 'brightness(0.9)' }}>
        <ImageWithFallback
          className="rounded-xl"
          key={REGIONS[selectedArea]?.name}
          src={REGIONS[selectedArea]?.image}
          alt={t(REGIONS[selectedArea]?.alt as string) || ''}
          layout="fixed"
          objectFit="cover"
          width={80}
          height={80}
          url=""
          fallbackSrc="/fallback-img-3704x2304.png"
        />
      </div>
      <div className="flex w-full flex-col justify-around">
        <p
          data-testid={testIds.mapCard.mapCardHeaderTitleText}
          className="text-title3 font-bold text-orange-700"
        >
          {t(REGIONS[selectedArea]?.mapCardTitle as string)}
        </p>
        <Link
          href={`/search/region/${REGIONS[selectedArea]?.path}?map=true&${searchQuery}`}
          passHref
          prefetch={false}
        >
          {regionText}
        </Link>
      </div>
    </>
  );
};

type MapCardPrefectureProps = {
  prefecture: {
    name: string;
    path: string;
  };
  onClick: (area: string) => void;
  searchQuery: string;
};

const MapCardPrefecture = ({ prefecture, onClick, searchQuery }: MapCardPrefectureProps) => {
  const { t } = useTranslation();

  return (
    <Link
      key={prefecture.path}
      href={`/search/prefecture/${prefecture.path}?map=true&${searchQuery}`}
      passHref
      prefetch={false}
    >
      <button
        type="button"
        onClick={() => onClick(prefecture.path)}
        className="flex cursor-pointer items-center justify-between"
      >
        <p className="my-1.5 text-small hover:underline">{t(prefecture.name)}</p>
        <BsChevronRight className="text-red mr-7 hidden size-3 stroke-2 text-orange-700 md:block" />
      </button>
    </Link>
  );
};

type Props = {
  selectedArea: RegionEnum;
  onClickArea: (area: string) => void;
  searchQuery: string;
};

const MapCard = ({ selectedArea, onClickArea, searchQuery }: Props) => {
  return (
    <div className="hidden items-center justify-end sm:flex" style={{ minWidth: 250 }}>
      <div className="flex h-84 w-96 flex-col rounded-xl bg-white p-6 shadow-2xl">
        <div className="flex flex-1 gap-6 pb-5">
          <MapCardHeader
            selectedArea={selectedArea}
            onSearchClick={onClickArea}
            searchQuery={searchQuery}
          />
        </div>
        <div className="flex flex-2 border-t border-gray-200 pt-4">
          <div
            data-testid={testIds.mapCard.mapCardPrefectureText}
            className="flex h-44 w-full flex-col flex-wrap"
          >
            {REGIONS[selectedArea]?.prefectures.map((prefecture) => (
              <MapCardPrefecture
                key={prefecture.path}
                prefecture={prefecture}
                onClick={onClickArea}
                searchQuery={searchQuery}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapCard;
